import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import type { Package, PackageListQuery } from '@mypoz/shared';
import {
  clampPageNumber,
  gramsToKg,
  hasActivePackageListQuery,
  isPackageLocked,
  pageCountFor,
  pageRange,
  parsePackageListQuery,
  parsePageNumber,
  parsePageSize,
} from '@mypoz/shared';
import { useTranslation } from '@mypoz/i18n';
import { Card, EmptyState, Icon, cn } from '@mypoz/ui';

import { PackageFilterBar } from '@/features/packages/components/PackageFilterBar.js';
import { PackageFormSheet } from '@/features/packages/components/PackageFormSheet.js';
import { PackageMessagesSheet } from '@/features/packages/components/PackageMessagesSheet.js';
import { PackageListRow } from '@/features/packages/components/PackageListRow.js';
import { Pager } from '@/components/Pager.js';
import { usePackages } from '@/features/packages/context.js';
import { usePackageListPage } from '@/features/packages/usePackageListPage.js';
import { useAutoTour, useTours } from '@/features/tour/index.js';
import { NEW_ORDER_ROUTE } from '@/shell/navigation.js';
import { buttonClass } from '@/lib/styles.js';
import { useFormat } from '@/lib/format.js';
import { useDebounced } from '@/lib/useDebounced.js';

/**
 * The Packages list — every parcel the customer has forwarded to a warehouse, whether it has
 * arrived yet, is sitting there, or has already gone out on an order.
 *
 * Filter state lives in the URL rather than component state (MYP-32), same reasoning as Orders:
 * the back button retraces what you were looking at, a refresh keeps it, and a narrowed view can
 * be sent to someone. The page and page size live there too (`?page=`, `?size=`).
 *
 * Paged by the server since MYP-126: each page, and each tab's count, is asked of `package_list`
 * with the filters the vendor can apply (tab → status codes, exact tracking number, goods type).
 * Nothing is filtered in the browser any more, because a filter applied to one page would quietly
 * miss every other page. See `list-paging.ts` for what the vendor was checked to support.
 *
 * Three outcomes for a request, not one: in flight, failed, answered — and two different
 * emptinesses after an answer: no packages at all, and nothing matching the filters.
 */
export function Packages() {
  const { t } = useTranslation();
  const fmt = useFormat();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const {
    packages: allPackages,
    status: allPackagesStatus,
    dictionariesStatus,
    goodsTypes,
    toPackages,
    listVersion,
    reload,
  } = usePackages();

  // Goods types are the vendor's dictionary (MYP-102); a `?goodsType=` reads as "all" until it
  // has loaded and resolves once it lands — the URL is only ever read here, so nothing is lost.
  const goodsTypeCodes = useMemo(() => goodsTypes.map((entry) => entry.code), [goodsTypes]);
  const query = useMemo(
    () => parsePackageListQuery(searchParams, goodsTypeCodes),
    [searchParams, goodsTypeCodes],
  );
  const requestedPage = parsePageNumber(searchParams);
  const pageSize = parsePageSize(searchParams);

  // The box updates at once; the vendor is asked once typing pauses (see `useDebounced`).
  const settledSearch = useDebounced(query.search);
  const asked = useMemo<PackageListQuery>(
    () => ({ ...query, search: settledSearch }),
    [query, settledSearch],
  );

  const list = usePackageListPage(asked, requestedPage, pageSize, listVersion);
  const onPage = useMemo(() => toPackages(list.rows), [toPackages, list.rows]);

  // One clock for every row's overdue-storage check, so nothing in this render disagrees with
  // itself about what "now" is.
  const now = useMemo(() => new Date(), []);

  const pageCount = pageCountFor(list.total, pageSize);
  const page = clampPageNumber(requestedPage, list.total, pageSize);

  /*
    A bookmarked `?page=` can outlive the packages that justified it, and a filter can shrink the
    list under it. Once the vendor has answered, an out-of-range page is replaced by the last one
    that exists, rather than showing an empty page.
  */
  useEffect(() => {
    if (list.status !== 'ready' || list.refreshing || requestedPage === page) return;
    setSearchParams(
      (prev) => {
        const params = new URLSearchParams(prev);
        if (page <= 1) params.delete('page');
        else params.set('page', String(page));
        return params;
      },
      { replace: true },
    );
  }, [list.status, list.refreshing, requestedPage, page, setSearchParams]);

  /*
    Any filter change drops `page`: a filter change is exactly the moment someone expects to be
    looking at the top of the list again. The page size is kept — it is a preference, not a filter.
  */
  const applyQuery = (next: PackageListQuery, options?: { replace?: boolean }) => {
    const params = new URLSearchParams();
    if (next.search.trim()) params.set('q', next.search);
    if (next.tab !== 'all') params.set('tab', next.tab);
    if (next.goodsType !== 'all') params.set('goodsType', next.goodsType);
    if (next.notOrdered) params.set('free', '1');
    const size = searchParams.get('size');
    if (size) params.set('size', size);
    setSearchParams(params, { replace: options?.replace ?? false });
  };

  const goToPage = (next: number) =>
    setSearchParams((prev) => {
      const params = new URLSearchParams(prev);
      if (next <= 1) params.delete('page');
      else params.set('page', String(next));
      return params;
    });

  // A new page size starts again at page 1: page 7 of 20-row pages is not page 7 of 50-row pages.
  const changePageSize = (size: number) =>
    setSearchParams((prev) => {
      const params = new URLSearchParams(prev);
      params.set('size', String(size));
      params.delete('page');
      return params;
    });

  const resetFilters = () =>
    setSearchParams(() => {
      const params = new URLSearchParams();
      const size = searchParams.get('size');
      if (size) params.set('size', size);
      return params;
    });

  const retry = () => {
    if (dictionariesStatus === 'error') reload();
    list.retry();
  };

  // MYP-33's slide-over lives in the same `useSearchParams` as the filters above rather than a
  // separate `useState`, for the same reason `editOrderRoute` uses a query param: which package is
  // open is view state, not a different resource, so a refresh or a shared link can restore it,
  // and a stale/removed id just degrades to closed instead of a dead panel.
  // Looked up on this page first, then in the account's full list (still held for the Dashboard
  // and Create order) — a link can name a package that is on another page.
  const panelPackageId = searchParams.get('package');
  const panelPackage = panelPackageId
    ? (onPage.find((item) => item.id === panelPackageId) ??
      allPackages.find((item) => item.id === panelPackageId))
    : undefined;

  // Lock status decides Edit vs. View, not whatever `panel` happens to say — so a hand-edited URL
  // can't ask to edit a package `isPackageLocked` says is fixed history. `isPackageLocked` is the
  // one source of truth for that; MYP-37 owns changing it later.
  //
  // `messages` is a third `panel` value (MYP-38), checked before the edit/view fallback: opening a
  // package's thread from the form sheet's "Messages" link rewrites this same query param rather
  // than adding separate state, so it closes the form sheet as a side effect — only one sheet is
  // ever open at a time, matching the mentor mockup, which shows the thread over the bare list
  // rather than stacked on top of the form.
  const panelMode = searchParams.get('panel');
  const panel =
    panelMode === 'add'
      ? { mode: 'add' as const, package: undefined }
      : panelMode === 'messages' && panelPackage
        ? { mode: 'messages' as const, package: panelPackage }
        : panelPackage
          ? {
              // `?panel=view` is honoured as asked: every row offers View now, including rows that
              // are perfectly editable, because reading what the warehouse recorded is its own
              // thing. Anything else means edit, and `isPackageLocked` still has the final say —
              // so a hand-edited `?panel=edit` on a locked package falls back to View rather than
              // opening a form over something that cannot change.
              mode:
                panelMode === 'view' || isPackageLocked(panelPackage)
                  ? ('view' as const)
                  : ('edit' as const),
              package: panelPackage,
            }
          : null;

  /*
    The package View mode may offer an Edit switch for — hoisted out of the JSX because narrowing
    on `panel.package` is lost inside a callback, and because the condition is worth naming: only
    a package that is both being read and still editable.
  */
  const editableInView =
    panel?.mode === 'view' && panel.package && !isPackageLocked(panel.package)
      ? panel.package
      : undefined;

  const openAddPanel = () =>
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.set('panel', 'add');
      next.delete('package');
      return next;
    });

  const openPackagePanel = (id: string, mode: 'view' | 'edit') =>
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.set('panel', mode);
      next.set('package', id);
      return next;
    });

  const openMessagesPanel = (id: string) =>
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.set('panel', 'messages');
      next.set('package', id);
      return next;
    });

  const closePanel = () =>
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.delete('panel');
      next.delete('package');
      return next;
    });

  /*
    The selection keeps each chosen package, keyed by id, so it survives moving between pages
    (MYP-126): page 2 is a new request, and the packages ticked on page 1 are no longer in it. The
    order form checks again which of them can still be ordered.
  */
  const [selected, setSelected] = useState<ReadonlyMap<string, Package>>(new Map());

  function toggleSelected(pkg: Package) {
    setSelected((current) => {
      const next = new Map(current);
      if (next.has(pkg.id)) next.delete(pkg.id);
      else next.set(pkg.id, pkg);
      return next;
    });
  }

  // After an add, edit or delete the account is re-read; a selected package that was deleted, or
  // has since gone onto an order, drops out of the selection then.
  useEffect(() => {
    if (allPackagesStatus !== 'ready') return;
    setSelected((current) => {
      const live = new Map(allPackages.map((pkg) => [pkg.id, pkg]));
      const next = new Map<string, Package>();
      for (const id of current.keys()) {
        const pkg = live.get(id);
        if (pkg && !isPackageLocked(pkg)) next.set(id, pkg);
      }
      return next.size === current.size ? current : next;
    });
  }, [allPackages, allPackagesStatus]);

  const selectedPackages = useMemo(() => [...selected.values()], [selected]);
  const selectedWeightGrams = selectedPackages.reduce(
    (total, pkg) => total + (pkg.weightGrams ?? 0),
    0,
  );

  // MYP-83: a first-time tour of this page, once the list has loaded — its last step points at it.
  const tours = useTours();
  useAutoTour('packages', list.status !== 'loading');

  function createOrderFromSelection() {
    // Passed as router state, not a query string — the same way CreateOrder later hands Orders a
    // submitted order number (`navigate(ORDERS_ROUTE, { state: { submitted } })`). A list of ids
    // has no business round-tripping through a URL a customer might bookmark or share.
    navigate(NEW_ORDER_ROUTE, { state: { packageIds: [...selected.keys()] } });
  }

  return (
    <>
      <div className="mb-4 flex items-start justify-between gap-4">
        <div className="min-w-0">
          <h1 className="text-2xl font-semibold text-ink">{t('packages.list.title')}</h1>
          {/* MYP-128: the subtitle gives way to the list on a phone. */}
          <p className="mt-1 hidden text-sm text-muted sm:block">{t('packages.list.subtitle')}</p>
        </div>

        <div className="flex shrink-0 items-center gap-2">
          <button
            type="button"
            onClick={() => tours.start('packages')}
            className="inline-flex min-h-11 min-w-11 items-center justify-center gap-1.5 text-sm font-semibold text-brand transition-colors hover:text-brand-dark focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand lg:min-h-0 lg:min-w-0"
          >
            <Icon name="help" size={18} />
            {/* Icon only on a phone, where the title needs the width; still named for a screen reader. */}
            <span className="sr-only sm:not-sr-only">{t('tour.howItWorks')}</span>
          </button>

          <button
            type="button"
            data-tour="packages-add"
            onClick={openAddPanel}
            className={buttonClass('primary', 'shrink-0 gap-1.5')}
          >
            <Icon name="add" size={18} />
            {t('packages.form.addTitle')}
          </button>
        </div>
      </div>

      {/*
        The bar shows once there is anything to filter: the account has packages, or a filter is
        already narrowing an empty answer (so it can be reset).
      */}
      {dictionariesStatus === 'ready' &&
      list.status === 'ready' &&
      (list.tabCounts.all > 0 || hasActivePackageListQuery(query)) ? (
        <PackageFilterBar
          query={query}
          tabCounts={list.tabCounts}
          goodsTypes={goodsTypes}
          onChange={applyQuery}
          onReset={resetFilters}
        />
      ) : null}

      {list.status === 'error' || dictionariesStatus === 'error' ? (
        <div className="grid justify-items-center gap-4 py-4">
          <EmptyState
            icon="cloud_off"
            title={t('packages.list.failed')}
            description={t('packages.list.failedBody')}
          />
          <button type="button" onClick={retry} className={buttonClass('secondary')}>
            <Icon name="refresh" size={18} />
            {t('packages.list.retry')}
          </button>
        </div>
      ) : list.status === 'loading' || dictionariesStatus === 'loading' ? (
        <EmptyState icon="hourglass_empty" title={t('packages.list.loading')} />
      ) : list.tabCounts.all === 0 && !hasActivePackageListQuery(query) ? (
        <div data-tour="packages-empty">
          <EmptyState
            icon="inventory_2"
            title={t('packages.list.empty')}
            description={t('packages.list.emptyBody')}
          />
        </div>
      ) : onPage.length === 0 ? (
        <div className="grid justify-items-center gap-4 py-4">
          <EmptyState
            icon="search_off"
            title={t('packages.filters.noMatches')}
            description={t('packages.filters.noMatchesBody')}
          />
          {hasActivePackageListQuery(query) ? (
            <button type="button" onClick={resetFilters} className={buttonClass('secondary')}>
              <Icon name="restart_alt" size={18} />
              {t('orders.filters.reset')}
            </button>
          ) : null}
        </div>
      ) : (
        <Card
          data-tour="packages-list"
          aria-busy={list.refreshing}
          className={cn(
            'divide-y divide-line p-0 transition-opacity',
            list.refreshing && 'opacity-60',
          )}
        >
          {onPage.map((pkg) => (
            <PackageListRow
              key={pkg.id}
              package={pkg}
              now={now}
              selected={selected.has(pkg.id)}
              onToggleSelect={isPackageLocked(pkg) ? undefined : () => toggleSelected(pkg)}
              onView={() => openPackagePanel(pkg.id, 'view')}
              onEdit={isPackageLocked(pkg) ? undefined : () => openPackagePanel(pkg.id, 'edit')}
            />
          ))}
        </Card>
      )}

      {list.status === 'ready' && dictionariesStatus === 'ready' && onPage.length > 0 ? (
        <Pager
          page={page}
          pageCount={pageCount}
          rangeStart={pageRange(page, pageSize, onPage.length).start}
          rangeEnd={pageRange(page, pageSize, onPage.length).end}
          total={list.total}
          onGo={goToPage}
          pageSize={pageSize}
          onPageSize={changePageSize}
        />
      ) : null}

      {panel?.mode === 'messages' ? (
        <PackageMessagesSheet key={panel.package.id} package={panel.package} onClose={closePanel} />
      ) : panel ? (
        <PackageFormSheet
          key={panel.package?.id ?? 'add'}
          package={panel.package}
          mode={panel.mode}
          onClose={closePanel}
          onOpenMessages={() => {
            if (panel.package) openMessagesPanel(panel.package.id);
          }}
          onEdit={editableInView ? () => openPackagePanel(editableInView.id, 'edit') : undefined}
        />
      ) : null}

      {/*
        Sticky rather than fixed: nested inside the page's own max-w-6xl column, it lines up with
        the card above it without having to know the sidebar's width the way a viewport-fixed bar
        would. Only ever holds packages this screen let you select, so "create order" can hand its
        ids straight to CreateOrder without CreateOrder needing to know where they came from.
      */}
      {selectedPackages.length > 0 ? (
        <div className="sticky bottom-4 z-10 mt-4 flex items-center justify-between gap-4 rounded-xl bg-ink-soft px-5 py-4 text-white shadow-raised">
          <div className="min-w-0">
            <p className="text-sm font-semibold tabular-nums">
              {t('packages.list.selection.summary', {
                count: selectedPackages.length,
                weight: t('orders.newOrder.weightKg', {
                  value: fmt.number(gramsToKg(selectedWeightGrams), 1),
                }),
              })}
            </p>
            <button
              type="button"
              onClick={() => setSelected(new Map())}
              className="text-sm text-white/70 underline-offset-2 hover:text-white hover:underline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white"
            >
              {t('packages.list.selection.clear')}
            </button>
          </div>

          <button
            type="button"
            onClick={createOrderFromSelection}
            className={buttonClass('primary', 'shrink-0 gap-1.5')}
          >
            {t('packages.list.selection.createOrder')}
            <Icon name="arrow_forward" size={18} />
          </button>
        </div>
      ) : null}
    </>
  );
}
